
'use client';

import { useState, useRef, FormEvent } from 'react';
import { LoaderCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { SectionWrapper } from '../section-wrapper';
import Link from 'next/link';
import { Label } from '../ui/label';

export function ContactSection() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const body = Object.fromEntries(formData.entries());

    try {
        const res = await fetch('/api/contact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });

        const data = await res.json();
        
        if (res.ok) {
            toast({
                title: '¡Mensaje Enviado!',
                description: data.message,
                variant: 'default',
            });
            if (data.sheetError) {
                toast({
                    title: 'Error al guardar en Google Sheet',
                    description: data.sheetError,
                    variant: 'destructive',
                });
            }
            formRef.current?.reset();
        } else {
            // Maneja errores de validación u otros errores del servidor
            const errorMessage = data.message || 'Ocurrió un error al enviar el mensaje.';
            toast({
                title: 'Error',
                description: errorMessage,
                variant: 'destructive',
            });
        }
    } catch (err) {
        console.error('Error en el formulario de contacto:', err);
        toast({
            title: 'Error de Red',
            description: 'No se pudo conectar con el servidor. Por favor, inténtalo más tarde.',
            variant: 'destructive',
        });
    } finally {
        setLoading(false);
    }
  };

  return (
    <SectionWrapper id="contact" className="bg-card text-card-foreground">
      <div className="container grid items-center justify-center gap-8 px-4 text-center md:px-6">
        <div className="space-y-3">
          <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl font-headline">CONTACTO</h1>
          <p className="mx-auto max-w-[600px] text-card-foreground/80 md:text-xl/relaxed">
            ¿Tienes alguna duda? ¡Escríbenos!
          </p>
        </div>
        <div className="mx-auto w-full max-w-lg space-y-4">
          <form 
            ref={formRef}
            onSubmit={handleSubmit}
            className="grid gap-4 text-left"
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input id="name" name="name" type="text" placeholder="Tu nombre" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" name="email" type="email" placeholder="tu@email.com" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Mensaje *</Label>
              <Textarea id="message" name="message" placeholder="Tu mensaje..." rows={5} required />
            </div>

            {/* Campo Honeypot para engañar a los bots */}
            <div className="opacity-0 absolute h-0 w-0 z-[-1]">
              <label htmlFor="honeypot">No rellenes este campo si eres humano</label>
              <input type="text" id="honeypot" name="honeypot" tabIndex={-1} autoComplete="off" />
            </div>
            
            <div className="text-center text-xs text-muted-foreground mt-2">
              <p>
                Al pulsar "Enviar Mensaje", aceptas nuestra{' '}
                <Link href="/politica-privacidad" className="underline hover:text-primary" target="_blank">
                  Política de Privacidad
                </Link>.
              </p>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90">
              {loading ? <LoaderCircle className="animate-spin" /> : "Enviar Mensaje"}
            </Button>
          </form>
          <div className="flex flex-col gap-8 mt-12 text-left">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h2 className="text-xl font-bold font-headline text-primary uppercase">¿En qué podemos ayudarte?</h2>
                <ul className="space-y-2 text-card-foreground/70">
                  <li>• Dudas sobre la adquisición de entradas.</li>
                  <li>• Información sobre la ubicación del evento en Valencia.</li>
                  <li>• Consultas sobre alérgenos y seguridad alimentaria en el evento.</li>
                  <li>• Sugerencias de ponentes o temáticas para el programa.</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-bold font-headline text-primary uppercase">Otras formas de contacto</h2>
                <p className="text-card-foreground/70">
                  Si prefieres una atención más directa para temas de prensa o colaboraciones especiales, puedes escribirnos directamente a nuestro correo electrónico:
                </p>
                <div className="flex items-center gap-2 text-primary font-bold">
                  <Mail className="h-5 w-5" />
                  <a href="mailto:info@singlutenexperienceday.com" className="hover:underline">
                    info@singlutenexperienceday.com
                  </a>
                </div>
              </div>
            </div>
            
            <div className="bg-primary/5 p-6 rounded-xl border border-primary/10">
              <p className="text-sm italic text-card-foreground/80">
                "Nuestro objetivo es que nadie se quede con dudas sobre la experiencia sin gluten más importante del año. Respondemos a todas las consultas en un plazo de 24-48 horas laborables."
              </p>
            </div>
          </div>
          <div className="mt-16 text-left max-w-4xl mx-auto space-y-8 border-t border-border/50 pt-12">
            <h2 className="text-2xl font-bold font-headline">Información Adicional para el Asistente</h2>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                    <h3 className="text-lg font-bold">Ubicación y Logística</h3>
                    <p className="text-card-foreground/70">
                        El evento se celebrará en Valencia. Próximamente confirmaremos el espacio exacto donde tendrá lugar esta nueva edición. Si vienes de fuera de la ciudad, te recomendamos estar atento a nuestras comunicaciones donde detallaremos las mejores opciones de transporte y alojamiento.
                    </p>
                </div>
                <div className="space-y-4">
                    <h3 className="text-lg font-bold">Seguridad y Salud</h3>
                    <p className="text-card-foreground/70">
                        La seguridad alimentaria es nuestra máxima prioridad. Todas las degustaciones y actividades de cocina en vivo están estrictamente controladas para garantizar la ausencia total de trazas de gluten. Si tienes alergias adicionales (como lactosa, frutos secos o huevo), no dudes en indicarlo en tu mensaje para que podamos brindarte una información más precisa sobre las opciones disponibles durante la jornada.
                    </p>
                </div>
            </div>
            <div className="space-y-4">
                <h3 className="text-lg font-bold">Prensa y Colaboradores</h3>
                <p className="text-card-foreground/70">
                    Si eres profesional de los medios de comunicación o representas a una marca interesada en formar parte del ecosistema Sin Gluten Experience Day, este es tu canal. Estamos abiertos a propuestas de colaboración que aporten valor a la comunidad celíaca y ayuden a difundir nuestro mensaje de normalización y disfrute compartido. Nuestro equipo de comunicación te atenderá para facilitarte materiales de prensa, entrevistas o dossiers de patrocinio.
                </p>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};
